import { Amplify } from 'aws-amplify';
import { Authenticator, ThemeProvider, Theme } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './amplify-auth-styles.css';
import { amplifyConfig, isCognitoEnabled } from '../amplify-config';

// Configure Amplify
if (isCognitoEnabled) {
  Amplify.configure(amplifyConfig);
}

// Custom theme to match your design
const theme: Theme = {
  name: 'zenmako-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: '#f3f4ff',
          20: '#e8ebff',
          40: '#c5ccff',
          60: '#a3adff',
          80: '#808eff',
          90: '#6d7aff',
          100: '#5a66ff', // Your purple button color
        },
      },
      background: {
        primary: '#ffffff',
        secondary: '#f8f9fa',
      },
    },
    components: {
      authenticator: {
        router: {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          borderWidth: '0',
        },
      },
      button: {
        primary: {
          backgroundColor: '#5a66ff',
          _hover: {
            backgroundColor: '#4a56ef',
          },
          _active: {
            backgroundColor: '#3a46df',
          },
        },
      },
      tabs: {
        item: {
          color: '#6b7280',
          _active: {
            color: '#5a66ff',
            borderColor: '#5a66ff',
          },
        },
      },
    },
  },
};

const formFields = {
  signIn: {
    username: {
      placeholder: 'example@gmail.com',
      label: 'E-mail',
    },
  },
  signUp: {
    email: {
      placeholder: 'example@gmail.com',
      label: 'E-mail',
      order: 1,
    },
    password: {
      label: 'Password',
      order: 2,
    },
    confirm_password: {
      label: 'Confirm Password',
      order: 3,
    },
  },
};

interface AmplifyAuthWrapperProps {
  children: preact.ComponentChildren;
}

export function AmplifyAuthWrapper({ children }: AmplifyAuthWrapperProps) {
  // If Cognito is not enabled (sandbox mode), just render children
  if (!isCognitoEnabled) {
    return <>{children}</>;
  }

  // Use Amplify Authenticator for staging/production with custom theme
  return (
    <ThemeProvider theme={theme}>
      <Authenticator
        formFields={formFields}
        components={{
          SignIn: {
            Header() {
              return (
                <>
                  {/* Logo */}
                  <div className="flex items-center gap-2 mb-8">
                    <img src="/favicon-32x32.png" alt="Zenmako" className="w-8 h-8" />
                    <h1 className="text-2xl font-bold text-black">zenmako</h1>
                  </div>
                  {/* Welcome heading */}
                  <div className="mb-8">
                    <h2 className="text-3xl font-semibold text-black mb-2">Welcome back</h2>
                  </div>
                </>
              );
            },
            Footer() {
              return <div />;
            },
          },
          SignUp: {
            Header() {
              return (
                <>
                  {/* Logo */}
                  <div className="flex items-center gap-2 mb-8">
                    <img src="/favicon-32x32.png" alt="Zenmako" className="w-8 h-8" />
                    <h1 className="text-2xl font-bold text-black">zenmako</h1>
                  </div>
                  {/* Create account heading */}
                  <div className="mb-8">
                    <h2 className="text-3xl font-semibold text-base-content mb-2">Start your journey</h2>
                  </div>
                </>
              );
            },
            Footer() {
              return <div />;
            },
          },
        }}
      >
        {() => (
          // When authenticated, just render children without the login layout
          <>{children}</>
        )}
      </Authenticator>
    </ThemeProvider>
  );
}
